import React, { useEffect, useState } from 'react';
import { HStack, Image, Pressable, VStack } from 'native-base';
import { Caption, H3 } from '~/shared/Typography';
import { PriceTag } from '~/pages/shop/components/PriceTag';
import { useAppNavigate } from '~/pages';
import {
    ProductVariant,
    useAddItemToCartMutation,
    useCreateCheckoutMutation,
    useDutchieStore,
    useI18n,
    useTheme,
} from '@springbig/shared';
import { Product } from '@springbig/shared/src/services/dutchie/schema-types';
import { useToast } from '~/hooks/toast';
import { VariantCard } from '~/pages/shop/components/VariantCard';
import { QuantityPicker } from '~/shared/QuantityPicker';
import { Card, Button, Modal } from '~/shared/Theme';

export type ProductCardProps = {
    item: Product;
};

export const ProductCard = ({ item }: ProductCardProps) => {

    const {
        radius_sm,
        radius_md,
    } = useTheme();

    const { navigate, params } = useAppNavigate();
    const _height = 240;
    const _width = _height / 2;

    const {
        menuType,
        setProduct,
        checkout,
        fullfillmentOption,
        retailer,
        setCheckout,
    } = useDutchieStore();

    const {
        mutate: addItem,
        data: newAddItem,
        isLoading: isLoadingAddItem
    } = useAddItemToCartMutation();

    const {
        mutate: createCheckout,
        data: newCheckout,
        isLoading: isLoadingCreateCheckout
    } = useCreateCheckoutMutation();

    const { errorToast } = useToast();
    const [isSelectVariantModal, setIsSelectVariantModal] = useState<boolean>(false);
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(item.variants[0]);
    const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
    const { t } = useI18n();

    useEffect(() => {

        if (newCheckout) {
            setCheckout(newCheckout.createCheckout);
            addItem({
                retailerId: retailer.id,
                checkoutId: newCheckout.createCheckout.id,
                quantity: selectedQuantity,
                option: item.variants[0].option,
                productId: item.id,
            });
        }
    }, [newCheckout]);

    useEffect(() => {

        if (newAddItem) {
            setCheckout(newAddItem.addItem);
        }
    }, [newAddItem]);


    const onAddItem = (): void => {

        if (checkout) {
            addItem({
                retailerId: retailer?.id,
                checkoutId: checkout.id,
                quantity: selectedQuantity,
                option: selectedVariant.option,
                productId: item.id,
            }, {
                onError: (error) => errorToast(error.data.errors[0].message)
            });
        }
        else {
            createCheckout({
                pricingType: menuType,
                orderType: fullfillmentOption.key,
                retailerId: retailer.id,
            });
        }
    };

    const onAddVariant = (): void => {

        onAddItem();
        setSelectedQuantity(1);
        setIsSelectVariantModal(false);
    };

    const tagColors = {
        hybrid: '#549976',
        sativa: '#8fb14b',
        sativa_hybrid: '#5eb14b',
        indica: '#c42f86',
        indica_hybrid: '#c42f3b',
        high_cbd: '#a42fc4',
        thc: '#4f2fc4',
        one_to_one: '#c4882f',
        two_to_one: '#c4882f',
        five_to_one: '#c4882f',
        ten_to_one: '#c4882f',
        twenty_to_one: '#c4882f',
        fifty_to_one: '#c4882f',
    };

    const percentageCalculator = (): string | null => {

        if (menuType === 'RECREATIONAL' && item.variants.find(variant => variant.specialPriceRec)) {
            const discountedPrice = item.variants[0].specialPriceRec || 0;
            const price = item.variants[0].priceRec || 100;
            const percentage = ((price - discountedPrice) / price) * 100;

            return Math.round(percentage) + '% off';
        }
        else if (menuType === 'MEDICAL' && item.variants.find(variant => variant.specialPriceMed)) {
            const discountedPrice = item.variants[0].specialPriceMed || 0;
            const price = item.variants[0].priceMed || 100;
            const percentage = ((price - discountedPrice) / price) * 100;

            return Math.round(percentage) + '% off';
        }

        return null;
    };

    const onSelectProduct = (): void => {

        setProduct(item);
        navigate(`w/${params.wallet_id}/shop/product/${item.id}`);
    };

    const displayFinalPrice = (variant: ProductVariant): string => {

        let finalPrice;
        if (menuType === 'RECREATIONAL') {
            finalPrice = variant.specialPriceRec ? variant.specialPriceRec : variant.priceRec;
        }

        if (menuType === 'MEDICAL') {
            finalPrice = variant.specialPriceMed ? variant.specialPriceMed : variant.priceMed;
        }

        return `$${(finalPrice * selectedQuantity).toFixed(2)}`;
    };

    const onCloseModal = (): void => {

        setSelectedQuantity(1);
        setIsSelectVariantModal(false);
    };

    const generateRandomKey = () => {

        return Math.random().toString(36).substr(2, 9);
    };

    const addButtonOnPress = () => {

        if (item && item.variants && item.variants.length > 1) {
            setIsSelectVariantModal(true);
        }
        else {
            onAddItem();
        }
    };

    return (
        <Pressable
            onPress={onSelectProduct}
            isDisabled={isLoadingAddItem || isLoadingCreateCheckout}
        >
            <Modal
                isOpen={isSelectVariantModal}
                closeable
                onClose={() => setIsSelectVariantModal(false)}
            >
                <Modal.Header>
                    <H3 m={0}>Select variant</H3>
                </Modal.Header>
                <Modal.Body>
                    <VStack space={4}>
                        <VStack space={4}>
                            { item.variants.map((variant) =>

                                <VariantCard
                                    key={generateRandomKey()}
                                    id={variant.id}
                                    price={menuType === 'RECREATIONAL' ? variant.priceRec : variant.priceMed}
                                    specialPrice={menuType === 'RECREATIONAL' ? variant.specialPriceRec : variant.specialPriceMed}
                                    name={variant.option}
                                    isSelected={variant.id === selectedVariant?.id}
                                    onPress={() => setSelectedVariant(variant)}
                                />
                            )}
                        </VStack>
                        <HStack justifyContent={'space-between'} alignItems={'center'}>
                            { selectedVariant.quantity > 1 &&
                                <QuantityPicker
                                    isNullable={false}
                                    quantity={selectedQuantity}
                                    maxQuantity={selectedVariant.quantity}
                                    setQuantity={(quantity) => setSelectedQuantity(quantity)}
                                    isLoading={isLoadingAddItem || isLoadingCreateCheckout}
                                    variant={'small'}
                                />
                            }
                            <Caption>
                                { displayFinalPrice(selectedVariant) }
                            </Caption>
                        </HStack>
                    </VStack>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        text={'shop.add_item'}
                        onPress={onAddVariant}
                    />
                </Modal.Footer>
            </Modal>
            <Card
                p={0}
                w={`${_width}px`}
                h={`${_height}px`}
                marginRight={4}
                position={'relative'}
            >

                { item.strainType && item.strainType !== 'NOT_APPLICABLE' &&
                    <Caption
                        position={'absolute'}
                        zIndex={1}
                        color={'white'}
                        fontSize={9}
                        right={2}
                        top={2}
                        px={1}
                        fontWeight={'400'}
                        rounded={radius_sm / 2}
                        style={{ backgroundColor: tagColors[item.strainType.toLowerCase()] }}
                    >
                        { item.strainType.replace('_', ' ') }
                    </Caption>
                }

                { percentageCalculator() &&
                    <Caption
                        position={'absolute'}
                        zIndex={1}
                        color={'white'}
                        fontSize={9}
                        left={2}
                        top={2}
                        px={1}
                        fontWeight={'400'}
                        rounded={radius_sm / 2}
                        style={{ backgroundColor: '#fd5959' }}
                    >
                        { percentageCalculator() }
                    </Caption>
                }

                <Image
                    src={item.images[0].url + `?w=${_width}&h=${_width}`}
                    w={`${_width}px`}
                    h={`${_width}px`}
                    roundedTop={radius_md}
                />

                <VStack h={`${_width}px`} p={2} space={1} justifyContent={'space-between'}>
                    <Caption noOfLines={2} fontSize={12} fontWeight={'bold'}>
                        { item.name }
                    </Caption>
                    <VStack space={1} flex={1}>
                        <Caption alignSelf={'center'} noOfLines={1} fontSize={10} minH={3} >
                            {item.potencyThc?.formatted ? t('shop.thc') + item.potencyThc.formatted + ' ' : ''}
                            {item.potencyCbd?.formatted ? t('shop.cbd') + item.potencyCbd.formatted : ''}
                        </Caption>
                        <HStack space={1} flexWrap={'wrap'}>
                            { item.variants.map((variant, index) =>

                                <Caption key={generateRandomKey()}>
                                    { variant.option !== 'N/A' ?
                                        <Caption fontSize={10}>{ variant.option }</Caption>
                                        :
                                        <></>
                                    }
                                    { item.variants.filter(v => v.option !== 'N/A').length > 1 && item.variants.length - 1 !== index ?
                                        <Caption fontSize={10}> |</Caption>
                                        :
                                        <></>
                                    }
                                </Caption>
                            )}
                        </HStack>
                    </VStack>
                    <HStack flex={1} justifyContent={'space-between'} alignItems={'center'}>
                        <PriceTag
                            price={menuType === 'MEDICAL' ?
                                item.variants[0].priceMed
                                :
                                item.variants[0].priceRec
                            }
                            discountedPrice={ menuType === 'MEDICAL' ?
                                item.variants[0].specialPriceMed
                                :
                                item.variants[0].specialPriceRec
                            }
                        />
                        <Button
                            text={'shop.add'}
                            modifier={'small'}
                            disabled={isLoadingAddItem || isLoadingCreateCheckout}
                            onPress={() => addButtonOnPress()}
                        />
                    </HStack>
                </VStack>
            </Card>
        </Pressable>
    );
};
